import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Button,
  Typography,
  Space,
  Modal,
  Input,
  Checkbox,
  message,
  Row,
  Col,
  Alert,
  Result,
} from 'antd';
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { checklistApi } from '@tradermind/shared/api';
import { formatCountdown } from '@tradermind/shared/utils';

const { Title, Text, Paragraph } = Typography;

export default function CooldownPage() {
  const { t } = useTranslation('checklist');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [bypassReason, setBypassReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [currentActivity, setCurrentActivity] = useState<any>(null);

  const { data } = useQuery({
    queryKey: ['cooldown', 'status'],
    queryFn: checklistApi.getCooldownStatus,
    refetchInterval: 1000,
  });

  const bypassMutation = useMutation({
    mutationFn: checklistApi.emergencyBypass,
    onSuccess: () => {
      message.success(t('cooldown_section.bypass_success'));
      queryClient.invalidateQueries({ queryKey: ['cooldown'] });
      setShowBypassModal(false);
      navigate('/dashboard');
    },
    onError: (error: any) => {
      const errorMessage = error?.error?.message || t('cooldown_section.bypass_failed');
      message.error(errorMessage);
    },
  });

  const completeActivityMutation = useMutation({
    mutationFn: checklistApi.completeActivity,
    onSuccess: (response) => {
      message.success(t('cooldown_section.activity_complete_msg', { points: response.data?.reward_points || 0 }));
      queryClient.invalidateQueries({ queryKey: ['cooldown'] });
      setShowActivityModal(false);
      setSelectedActivity(null);
    },
    onError: () => {
      message.error(t('cooldown_section.activity_failed'));
    },
  });

  const cooldown = data?.data;

  const handleBypass = () => {
    if (!bypassReason.trim()) {
      message.warning(t('cooldown_section.bypass_reason_required'));
      return;
    }
    if (bypassReason.trim().length < 10) {
      message.warning(t('cooldown_section.bypass_reason_min'));
      return;
    }
    if (!acknowledged) {
      message.warning(t('cooldown_section.bypass_acknowledge_required'));
      return;
    }
    bypassMutation.mutate({
      reason: bypassReason,
      acknowledgment: acknowledged,
    });
  };

  if (!cooldown?.is_in_cooldown) {
    return (
      <Result
        icon={<ClockCircleOutlined style={{ color: '#10B981' }} />}
        title={t('cooldown_section.no_active_title')}
        subTitle={t('cooldown_section.no_active_subtitle')}
        extra={
          <Button type="primary" onClick={() => navigate('/dashboard')}>
            {t('cooldown_section.back_home')}
          </Button>
        }
      />
    );
  }

  const activities = [
    {
      id: 'breathing',
      title: t('cooldown_section.activities.breathing.title'),
      description: t('cooldown_section.activities.breathing.description'),
      icon: '🧘',
      duration: t('cooldown_section.activities.breathing.duration'),
    },
    {
      id: 'quotes',
      title: t('cooldown_section.activities.quotes.title'),
      description: t('cooldown_section.activities.quotes.description'),
      icon: '📚',
      duration: t('cooldown_section.activities.quotes.duration'),
    },
    {
      id: 'review',
      title: t('cooldown_section.activities.review.title'),
      description: t('cooldown_section.activities.review.description'),
      icon: '📊',
      duration: t('cooldown_section.activities.review.duration'),
    },
    {
      id: 'walk',
      title: t('cooldown_section.activities.walk.title'),
      description: t('cooldown_section.activities.walk.description'),
      icon: '🚶',
      duration: t('cooldown_section.activities.walk.duration'),
    },
  ];

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>⏰</div>
        <Title level={2}>{t('cooldown_section.title')}</Title>
        <div
          style={{
            fontSize: 48,
            fontWeight: 700,
            color: '#EF4444',
            marginBottom: 16,
          }}
        >
          {formatCountdown(cooldown.remaining_seconds || 0)}
        </div>
        <Text type="secondary">{t('cooldown_section.wait_message')}</Text>
      </div>

      {cooldown.trigger_reason && (
        <Alert
          message={t('cooldown_section.trigger_reason')}
          description={cooldown.trigger_reason}
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {cooldown.trigger_data && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          {cooldown.trigger_data.consecutive_losses && (
            <Col span={8}>
              <Card size="small">
                <Text type="secondary">{t('cooldown_section.consecutive_losses')}</Text>
                <div style={{ fontSize: 24, fontWeight: 600, color: '#EF4444' }}>
                  {cooldown.trigger_data.consecutive_losses} {t('cooldown_section.times')}
                </div>
              </Card>
            </Col>
          )}
          {cooldown.trigger_data.total_loss_amount && (
            <Col span={8}>
              <Card size="small">
                <Text type="secondary">{t('cooldown_section.total_loss')}</Text>
                <div style={{ fontSize: 24, fontWeight: 600, color: '#EF4444' }}>
                  ${cooldown.trigger_data.total_loss_amount.toFixed(2)}
                </div>
              </Card>
            </Col>
          )}
          {cooldown.trigger_data.emotion_intensity && (
            <Col span={8}>
              <Card size="small">
                <Text type="secondary">{t('cooldown_section.emotion_intensity')}</Text>
                <div style={{ fontSize: 24, fontWeight: 600, color: '#F59E0B' }}>
                  {cooldown.trigger_data.emotion_intensity}/10
                </div>
              </Card>
            </Col>
          )}
        </Row>
      )}

      <Card title={`🧘 ${t('cooldown_section.activities_title')}`} style={{ marginBottom: 24 }}>
        <Paragraph type="secondary">{t('cooldown_section.activities_desc')}</Paragraph>
        <Row gutter={[16, 16]}>
          {activities.map((activity) => (
            <Col xs={24} sm={12} key={activity.id}>
              <Card
                hoverable
                onClick={() => setSelectedActivity(activity.id)}
                style={{
                  border:
                    selectedActivity === activity.id ? '2px solid #3B82F6' : '1px solid #e5e7eb',
                }}
              >
                <Space>
                  <div style={{ fontSize: 32 }}>{activity.icon}</div>
                  <div>
                    <div style={{ fontWeight: 500 }}>{activity.title}</div>
                    <Text type="secondary">{activity.description}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('cooldown_section.estimated')} {activity.duration}
                      </Text>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        {selectedActivity && (
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Button 
              type="primary" 
              icon={<PlayCircleOutlined />} 
              size="large"
              onClick={() => {
                const activity = activities.find((a) => a.id === selectedActivity);
                setCurrentActivity(activity);
                setShowActivityModal(true);
              }}
            >
              {t('cooldown_section.start_activity')} {activities.find((a) => a.id === selectedActivity)?.title}
            </Button>
          </div>
        )}
      </Card>

      <Card>
        <div style={{ textAlign: 'center' }}>
          <Text type="secondary">{t('cooldown_section.emergency_hint')}</Text>
          <div style={{ marginTop: 16 }}>
            <Button danger onClick={() => setShowBypassModal(true)}>
              {t('cooldown_section.emergency_bypass')}
            </Button>
          </div>
        </div>
      </Card>

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#EF4444' }} />
            {t('cooldown_section.emergency_bypass_title')}
          </Space>
        }
        open={showBypassModal}
        onCancel={() => setShowBypassModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowBypassModal(false)}>
            {t('common:cancel')}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={bypassMutation.isPending}
            onClick={handleBypass}
          >
            {t('cooldown_section.confirm_bypass')}
          </Button>,
        ]}
      >
        <Alert
          message={t('cooldown_section.warning')}
          description={t('cooldown_section.bypass_warning')}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 16 }}>
          <Text strong>{t('cooldown_section.bypass_reason_label')}</Text>
          <Input.TextArea
            value={bypassReason}
            onChange={(e) => setBypassReason(e.target.value)}
            rows={3}
            placeholder={t('cooldown_section.bypass_reason_placeholder')}
            style={{ marginTop: 8 }}
          />
        </div>

        <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}>
          {t('cooldown_section.bypass_acknowledge')}
        </Checkbox>
      </Modal>

      <Modal
        title={currentActivity?.icon + ' ' + currentActivity?.title}
        open={showActivityModal}
        onCancel={() => setShowActivityModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowActivityModal(false)}>
            {t('common:cancel')}
          </Button>,
          <Button
            key="complete"
            type="primary"
            loading={completeActivityMutation.isPending}
            onClick={() => {
              if (cooldown?.current_session?.id && currentActivity?.id) {
                completeActivityMutation.mutate({
                  cooldown_id: cooldown.current_session.id,
                  activity_id: currentActivity.id,
                });
              }
            }}
          >
            {t('cooldown_section.complete_activity')}
          </Button>,
        ]}
        width={600}
      >
        {currentActivity?.id === 'breathing' && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 24 }}>🧘</div>
            <Title level={3}>{t('cooldown_section.activities.breathing.method_title')}</Title>
            <Paragraph>
              1. {t('cooldown_section.activities.breathing.step1')}<br />
              2. {t('cooldown_section.activities.breathing.step2')}<br />
              3. {t('cooldown_section.activities.breathing.step3')}<br />
              4. {t('cooldown_section.activities.breathing.step4')}
            </Paragraph>
            <Alert
              message={t('common:tip', '提示')}
              description={t('cooldown_section.activities.breathing.tip')}
              type="info"
              showIcon
            />
          </div>
        )}
        {currentActivity?.id === 'quotes' && (
          <div style={{ padding: '20px' }}>
            <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 24 }}>📚</div>
            <Card style={{ marginBottom: 16, background: '#f0f9ff' }}>
              <Paragraph style={{ fontSize: 16, fontStyle: 'italic', margin: 0 }}>
                "{t('cooldown_section.activities.quotes.quote1')}"
              </Paragraph>
              <Text type="secondary" style={{ fontSize: 12 }}>— {t('cooldown_section.activities.quotes.quote1_author')}</Text>
            </Card>
            <Card style={{ marginBottom: 16, background: '#f0fdf4' }}>
              <Paragraph style={{ fontSize: 16, fontStyle: 'italic', margin: 0 }}>
                "{t('cooldown_section.activities.quotes.quote2')}"
              </Paragraph>
              <Text type="secondary" style={{ fontSize: 12 }}>— {t('cooldown_section.activities.quotes.quote2_author')}</Text>
            </Card>
            <Card style={{ background: '#fef3c7' }}>
              <Paragraph style={{ fontSize: 16, fontStyle: 'italic', margin: 0 }}>
                "{t('cooldown_section.activities.quotes.quote3')}"
              </Paragraph>
              <Text type="secondary" style={{ fontSize: 12 }}>— {t('cooldown_section.activities.quotes.quote3_author')}</Text>
            </Card>
          </div>
        )}
        {currentActivity?.id === 'review' && (
          <div style={{ padding: '20px' }}>
            <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 24 }}>📊</div>
            <Title level={4}>{t('cooldown_section.activities.review.think_title')}</Title>
            <Paragraph>
              {t('cooldown_section.activities.review.think_intro')}
            </Paragraph>
            <ul style={{ lineHeight: 2 }}>
              <li>{t('cooldown_section.activities.review.q1')}</li>
              <li>{t('cooldown_section.activities.review.q2')}</li>
              <li>{t('cooldown_section.activities.review.q3')}</li>
              <li>{t('cooldown_section.activities.review.q4')}</li>
              <li>{t('cooldown_section.activities.review.q5')}</li>
            </ul>
            <Alert
              message={t('common:suggestion', '建议')}
              description={t('cooldown_section.activities.review.suggestion')}
              type="warning"
              showIcon
            />
          </div>
        )}
        {currentActivity?.id === 'walk' && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 24 }}>🚶</div>
            <Title level={3}>{t('cooldown_section.activities.walk.rest_title')}</Title>
            <Paragraph>
              {t('cooldown_section.activities.walk.suggest_intro')}
            </Paragraph>
            <ul style={{ textAlign: 'left', display: 'inline-block', lineHeight: 2 }}>
              <li>{t('cooldown_section.activities.walk.s1')}</li>
              <li>{t('cooldown_section.activities.walk.s2')}</li>
              <li>{t('cooldown_section.activities.walk.s3')}</li>
              <li>{t('cooldown_section.activities.walk.s4')}</li>
            </ul>
            <Alert
              message={t('common:science', '科学依据')}
              description={t('cooldown_section.activities.walk.science')}
              type="info"
              showIcon
              style={{ marginTop: 20 }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
